import { Booking, Settings } from '../types/booking'
import { ACTIVE_STATUSES, toMinutes } from './availability'

export type Placed = {
  booking: Booking
  start: number
  end: number
  lane: number
  lanes: number
}

/** Greedy lane assignment so overlapping bookings sit side by side. */
export function placeBookings(bookings: Booking[]): Placed[] {
  const sorted = [...bookings].sort((a, b) => toMinutes(a.time) - toMinutes(b.time))
  const laneEnds: number[] = []
  const placed: Omit<Placed, 'lanes'>[] = []

  sorted.forEach((booking) => {
    const start = toMinutes(booking.time)
    const end = start + booking.durationMinutes
    let lane = laneEnds.findIndex((laneEnd) => laneEnd <= start)
    if (lane === -1) {
      lane = laneEnds.length
      laneEnds.push(end)
    } else {
      laneEnds[lane] = end
    }
    placed.push({ booking, start, end, lane })
  })

  return placed.map((item) => {
    const overlapping = placed.filter((other) => item.start < other.end && item.end > other.start)
    const lanes = Math.max(...overlapping.map((o) => o.lane)) + 1
    return { ...item, lanes }
  })
}

export function dayWindow(dates: string[], settings: Settings) {
  const opens: number[] = []
  const closes: number[] = []

  dates.forEach((iso) => {
    const hours = settings.hours[new Date(`${iso}T00:00:00`).getDay()]
    if (hours?.open && hours?.close) {
      opens.push(toMinutes(hours.open))
      closes.push(toMinutes(hours.close))
    }
  })

  const start = opens.length > 0 ? Math.min(...opens) : 9 * 60
  const end = closes.length > 0 ? Math.max(...closes) : 18 * 60
  return { start: start - 30, end: end + 30 }
}

export function dayStats(bookings: Booking[], iso: string) {
  const rows = bookings.filter((b) => b.date === iso && ACTIVE_STATUSES.includes(b.status))
  return {
    count: rows.length,
    revenue: rows.reduce((sum, b) => sum + b.amount, 0),
    minutes: rows.reduce((sum, b) => sum + b.durationMinutes, 0),
    pending: rows.filter((b) => b.status === 'pending').length,
  }
}

export function statusAccent(booking: Booking) {
  if (booking.status === 'cancelled') return 'border-edge bg-panel text-dim line-through'
  if (booking.status === 'pending') return 'border-amber-400/50 bg-amber-400/10 text-amber-100'
  if (booking.status === 'completed') return 'border-emerald-400/45 bg-emerald-400/10 text-emerald-100'
  return 'border-ember/55 bg-ember/15 text-chalk'
}
